import type {
  CautionaryProfile,
  StructuredRubricFields,
} from "@shared/labs/level-assessment-model";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { C, CELL_TARGET, cellStatus, DM, MONO, UI } from "../results/ui-tokens";
import {
  type AxisDraft,
  addReferenceProfileIn,
  type CellMap,
  type FieldsMap,
  patchReferenceProfileIn,
  removeReferenceProfileIn,
  setBoundaryIn,
  setStructuredCell,
} from "./matrix-cells";

// The matrix data-model types live in `matrix-cells` (the data module) to avoid
// a component↔helper import cycle. Re-export them here so the existing consumers
// (wizard, discipline step, CSV import) keep importing them from this module.
export type { AxisDraft, CellMap, FieldsMap };

/**
 * Reusable competency matrix editor — one matrix of (axes × levels) whose cells
 * are the per-(axis, level) rubric descriptions. Used by the create wizard for
 * BOTH the cross-competency block and each discipline's vertical-competency
 * block. The level range is model-wide (shared across every matrix), so the
 * level add/remove affordances live OUTSIDE this component — here we only render
 * the level labels + the axis editing + cells.
 *
 * Rendering model (axis-rail + level-accordion): a left rail lists the axes (one
 * selectable at a time); the right workspace card edits the selected axis's
 * identity and its per-level rubric cells as an accordion. The component owns
 * the Edit/Review mode toggle and the overall "{filled} de {total} células
 * preenchidas" progress text internally — consumers must NOT render their own.
 *
 * Structured-field mode: when both `fields` and `onSetFields` are provided the
 * component surfaces the full Pillar 2/3/4 structured editor (axis-definition
 * collapsible + per-cell evidence + reference profiles + "cruza quando"
 * boundaries). When either is absent the component behaves byte-identically to
 * the pre-structured era — def-only editing. CSV preview relies on this gate.
 */

export function emptyAxis(): AxisDraft {
  return { label: "", description: "", weight: 1 };
}

export interface CompetencyMatrixProps {
  axes: AxisDraft[];
  cells: CellMap;
  levels: number[];
  /** Optional model-wide level names (keyed by level number as a string).
   *  Rendered under the `L{n}` label so the matrix shows e.g. "L5 / Senior". */
  levelNames?: Record<string, string>;
  disabled: boolean;
  /** Namespaces every data-testid so cross + each discipline stay distinct. */
  testidPrefix: string;
  onAddAxis: () => void;
  onRemoveAxis: (idx: number) => void;
  onUpdateAxis: (idx: number, patch: Partial<AxisDraft>) => void;
  onSetCell: (axisIdx: number, level: number, value: string) => void;
  /** Minimum axes that must remain (1). */
  minAxes?: number;
  // ── Structured-field editing (Pillar 2/3/4) ─────────────────────────────
  /** When BOTH provided, the editor surfaces the structured Pillar 2/3/4 fields.
   *  Omit (e.g. CSV preview) for a def-only matrix — render exactly as today. */
  fields?: FieldsMap;
  onSetFields?: (axisIdx: number, next: StructuredRubricFields) => void;
}

function cellLen(cells: CellMap, axisIdx: number, level: number): number {
  return (cells[axisIdx]?.[level] ?? "").length;
}

/** How many of an axis's levels have any text. */
function axisFilled(cells: CellMap, axisIdx: number, levels: number[]): number {
  let n = 0;
  for (const level of levels) if (cellLen(cells, axisIdx, level) > 0) n++;
  return n;
}

export function CompetencyMatrix(props: CompetencyMatrixProps) {
  const { axes, cells, levels, disabled, testidPrefix } = props;
  const minAxes = props.minAxes ?? 1;
  // Structured mode is only active when the consumer wires BOTH props.
  const isStructured = props.fields !== undefined && props.onSetFields !== undefined;

  const [mode, setMode] = useState<"edit" | "review">("edit");
  const [selectedAxisIdx, setSelectedAxisIdx] = useState(0);
  const [expandedLevel, setExpandedLevel] = useState<number | null>(null);

  // Clamp selection so axis removals never leave us pointing past the end.
  const axisIdx = Math.max(0, Math.min(selectedAxisIdx, axes.length - 1));
  const isEdit = mode === "edit";

  // Overall progress across the whole matrix.
  const totalCells = axes.length * levels.length;
  let filledCells = 0;
  for (let a = 0; a < axes.length; a++) {
    for (const level of levels) if (cellLen(cells, a, level) > 0) filledCells++;
  }

  const selected = axes[axisIdx];
  const selectedLabel = selected?.label.trim() ?? "";

  return (
    <div data-testid={`${testidPrefix}-matrix-table`}>
      {/* Top bar: overall progress + Edit/Review segmented toggle */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
          marginBottom: 16,
        }}
      >
        <span style={{ ...UI, fontSize: 13, fontWeight: 500, color: C.ink70 }}>
          {filledCells} de {totalCells} células preenchidas
        </span>
        <div
          style={{
            display: "flex",
            background: C.white,
            border: `1px solid ${C.border}`,
            borderRadius: 999,
            padding: 3,
            gap: 2,
          }}
        >
          <SegmentButton
            active={isEdit}
            activeBg={C.red}
            onClick={() => setMode("edit")}
            disabled={disabled}
            testId={`${testidPrefix}-matrix-mode-edit`}
          >
            Editar
          </SegmentButton>
          <SegmentButton
            active={!isEdit}
            activeBg={C.ink}
            onClick={() => setMode("review")}
            testId={`${testidPrefix}-matrix-mode-review`}
          >
            Revisar
          </SegmentButton>
        </div>
      </div>

      {/* Body: axis rail + workspace */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "264px 1fr",
          gap: 20,
          alignItems: "start",
        }}
      >
        {/* ── Axis rail ───────────────────────────────────────────── */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            position: "sticky",
            top: 16,
          }}
        >
          <div
            style={{
              ...UI,
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: "0",
              textTransform: "uppercase",
              color: C.ink50,
              padding: "0 4px 2px",
            }}
          >
            Eixos · {axes.length}
          </div>
          {axes.map((axis, i) => {
            const active = i === axisIdx;
            const filled = axisFilled(cells, i, levels);
            const pct = levels.length ? filled / levels.length : 0;
            return (
              <button
                // biome-ignore lint/suspicious/noArrayIndexKey: axes are ordered by position, no stable id until typed.
                key={i}
                type="button"
                onClick={() => {
                  setSelectedAxisIdx(i);
                  setExpandedLevel(null);
                }}
                data-testid={`${testidPrefix}-matrix-axis-rail-${i}`}
                style={{
                  textAlign: "left",
                  cursor: "pointer",
                  border: `1px solid ${active ? C.red : C.border}`,
                  background: active ? C.white : C.paper,
                  borderRadius: 12,
                  padding: "12px 14px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 9,
                  transition: "all 160ms",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                  }}
                >
                  <span
                    style={{
                      ...DM,
                      fontWeight: 700,
                      fontSize: 14.5,
                      color: active ? C.ink : C.ink70,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {axis.label.trim() || "Novo eixo"}
                  </span>
                  <span
                    style={{
                      ...MONO,
                      fontSize: 11,
                      color: active ? C.red : C.ink50,
                      flex: "none",
                    }}
                  >
                    {filled}/{levels.length}
                  </span>
                </div>
                <div
                  style={{
                    height: 5,
                    borderRadius: 999,
                    background: C.ink5,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: `${Math.round(pct * 100)}%`,
                      background: active ? C.red : C.ink20,
                      transition: "width 320ms",
                    }}
                  />
                </div>
              </button>
            );
          })}
          <button
            type="button"
            onClick={props.onAddAxis}
            disabled={disabled}
            data-testid={`${testidPrefix}-matrix-add-axis`}
            title="Adicionar novo eixo"
            style={{
              ...UI,
              border: `1px dashed ${C.ink20}`,
              background: "transparent",
              borderRadius: 12,
              cursor: disabled ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              color: C.ink50,
              padding: 11,
              fontSize: 12.5,
              fontWeight: 600,
              opacity: disabled ? 0.6 : 1,
            }}
          >
            <PlusIcon />
            eixo
          </button>
        </div>

        {/* ── Axis workspace ──────────────────────────────────────── */}
        <div
          style={{
            background: C.white,
            border: `1px solid ${C.border}`,
            borderRadius: 16,
            overflow: "hidden",
          }}
        >
          {selected ? (
            <>
              <AxisWorkspaceHeader
                axis={selected}
                axisIdx={axisIdx}
                filled={axisFilled(cells, axisIdx, levels)}
                levelCount={levels.length}
                canRemove={axes.length > minAxes}
                disabled={disabled}
                testidPrefix={testidPrefix}
                onChange={(patch) => props.onUpdateAxis(axisIdx, patch)}
                onRemove={() => {
                  props.onRemoveAxis(axisIdx);
                  setExpandedLevel(null);
                }}
              />

              {/* ── Axis-definition collapsible (structured mode only) ─ */}
              {isStructured && (
                <AxisDefinitionSection
                  axis={selected}
                  axisIdx={axisIdx}
                  levels={levels}
                  levelNames={props.levelNames}
                  disabled={disabled}
                  isEdit={isEdit}
                  testidPrefix={testidPrefix}
                  onChange={(patch) => props.onUpdateAxis(axisIdx, patch)}
                />
              )}

              <div style={{ display: "flex", flexDirection: "column" }}>
                {levels.map((level) => {
                  const len = cellLen(cells, axisIdx, level);
                  const status = cellStatus(len);
                  const expanded = expandedLevel === level;
                  const text = cells[axisIdx]?.[level] ?? "";
                  const name = props.levelNames?.[String(level)]?.trim();
                  const dot = status === "empty" ? C.amberFg : status === "good" ? C.green : C.red;
                  const placeholder = selectedLabel
                    ? `O que ${selectedLabel} exige em L${level}?`
                    : "Defina o label do eixo acima";

                  // Per-axis structured fields for this level (structured mode).
                  const axisFields: StructuredRubricFields = props.fields?.[axisIdx] ?? {};
                  const cellFields = axisFields.cells?.[String(level)] ?? {};
                  const boundaryText = axisFields.boundaries?.[String(level)] ?? "";
                  const isLastLevel = level === levels[levels.length - 1];

                  return (
                    <div
                      key={level}
                      data-testid={`${testidPrefix}-matrix-row-${level}`}
                      style={{ borderBottom: `1px solid ${C.ink5}` }}
                    >
                      <button
                        type="button"
                        onClick={() => setExpandedLevel(expanded ? null : level)}
                        style={{
                          width: "100%",
                          textAlign: "left",
                          cursor: "pointer",
                          border: 0,
                          background: expanded ? C.red5 : C.white,
                          padding: "14px 24px",
                          display: "flex",
                          alignItems: "center",
                          gap: 16,
                          transition: "background 140ms",
                        }}
                      >
                        <div
                          style={{
                            flex: "none",
                            width: 104,
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                          }}
                        >
                          <span
                            style={{
                              ...DM,
                              display: "flex",
                              alignItems: "center",
                              gap: 7,
                              fontWeight: 700,
                              fontSize: 15,
                              color: C.ink,
                            }}
                          >
                            <span
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: 999,
                                background: dot,
                                flex: "none",
                              }}
                            />
                            L{level}
                          </span>
                          {name && (
                            <span
                              style={{
                                ...UI,
                                fontSize: 11,
                                color: C.ink50,
                                paddingLeft: 15,
                              }}
                            >
                              {name}
                            </span>
                          )}
                        </div>
                        {!expanded && (
                          <span
                            style={{
                              ...UI,
                              flex: 1,
                              minWidth: 0,
                              fontSize: 13,
                              lineHeight: "18px",
                              color: len > 0 ? C.ink70 : C.ink50,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {len > 0 ? text : "Vazio — clique para escrever"}
                          </span>
                        )}
                        {expanded && <span style={{ flex: 1 }} />}
                        <span
                          style={{
                            ...MONO,
                            flex: "none",
                            fontSize: 10.5,
                            color: len > 0 ? C.ink50 : C.amberFg,
                          }}
                        >
                          {len > 0 ? `${len} c` : "vazio"}
                        </span>
                        <ChevronIcon rotated={expanded} color={expanded ? C.red : C.ink20} />
                      </button>
                      {expanded && (
                        <div style={{ padding: "4px 24px 20px" }}>
                          {isEdit ? (
                            <>
                              <Textarea
                                value={text}
                                onChange={(e) => props.onSetCell(axisIdx, level, e.target.value)}
                                placeholder={placeholder}
                                disabled={disabled}
                                data-testid={`${testidPrefix}-matrix-cell-${axisIdx}-${level}`}
                                style={{
                                  ...DM,
                                  width: "100%",
                                  minHeight: 180,
                                  resize: "vertical",
                                  padding: "14px 16px",
                                  borderRadius: 10,
                                  border: `1px solid ${C.ink10}`,
                                  fontSize: 15,
                                  lineHeight: "24px",
                                  color: C.ink,
                                }}
                              />
                              <CellProgress len={len} />

                              {/* ── Structured per-cell fields (structured mode only) */}
                              {isStructured && (
                                <StructuredCellFields
                                  axisIdx={axisIdx}
                                  level={level}
                                  cellFields={cellFields}
                                  boundaryText={boundaryText}
                                  isLastLevel={isLastLevel}
                                  disabled={disabled}
                                  testidPrefix={testidPrefix}
                                  onSetFields={(next) => props.onSetFields!(axisIdx, next)}
                                  axisFields={axisFields}
                                />
                              )}
                            </>
                          ) : len > 0 ? (
                            <>
                              <div
                                style={{
                                  ...DM,
                                  fontSize: 15,
                                  lineHeight: "24px",
                                  color: C.ink,
                                  minHeight: 60,
                                  padding: "4px 0",
                                  whiteSpace: "pre-wrap",
                                }}
                              >
                                {text}
                              </div>

                              {/* ── Structured per-cell fields read-only (review) */}
                              {isStructured && (
                                <StructuredCellFieldsReadOnly
                                  axisIdx={axisIdx}
                                  level={level}
                                  cellFields={cellFields}
                                  boundaryText={boundaryText}
                                  isLastLevel={isLastLevel}
                                  testidPrefix={testidPrefix}
                                />
                              )}
                            </>
                          ) : (
                            <div
                              style={{
                                ...DM,
                                fontSize: 15,
                                lineHeight: "24px",
                                color: C.ink50,
                                minHeight: 60,
                                padding: "4px 0",
                              }}
                            >
                              Esta célula ainda está vazia.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Axis-definition collapsible (structured mode only)
// ─────────────────────────────────────────────────────────────────────────────

function AxisDefinitionSection({
  axis,
  axisIdx,
  levels,
  levelNames,
  disabled,
  isEdit,
  testidPrefix,
  onChange,
}: {
  axis: AxisDraft;
  axisIdx: number;
  levels: number[];
  levelNames?: Record<string, string>;
  disabled: boolean;
  isEdit: boolean;
  testidPrefix: string;
  onChange: (patch: Partial<AxisDraft>) => void;
}) {
  const [open, setOpen] = useState(true);

  // Whether any pillar field has content — drives the status badge.
  const hasContent =
    axis.notAbout?.trim() ||
    axis.baseline != null ||
    axis.falsePositives?.trim() ||
    axis.hardCap?.trim() ||
    (axis.cautionaryProfiles ?? []).length > 0;

  return (
    <div
      style={{
        borderBottom: `1px solid ${C.borderSoft}`,
      }}
    >
      {/* Toggle header */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: "100%",
          textAlign: "left",
          border: 0,
          background: open ? "rgba(243,53,75,0.03)" : C.white,
          borderBottom: open ? `1px solid ${open ? "rgba(243,53,75,0.15)" : C.ink5}` : undefined,
          padding: "12px 24px",
          display: "flex",
          alignItems: "center",
          gap: 10,
          cursor: "pointer",
          transition: "background 140ms",
        }}
      >
        <span style={{ ...UI, fontSize: 12, fontWeight: 700, color: C.ink70, flex: 1 }}>
          Definição do eixo
        </span>
        <span
          style={{
            ...MONO,
            fontSize: 10,
            padding: "2px 7px",
            borderRadius: 999,
            background: hasContent ? "rgba(38,142,108,0.1)" : "rgba(154,123,46,0.1)",
            color: hasContent ? C.green : C.amberFg,
          }}
        >
          {hasContent ? "preenchida" : "incompleta"}
        </span>
        <ChevronIcon rotated={open} color={open ? C.red : C.ink20} />
      </button>

      {open && (
        <div
          style={{ padding: "16px 24px 20px", display: "flex", flexDirection: "column", gap: 16 }}
        >
          {/* notAbout */}
          <StructuredField label="Não é sobre">
            {isEdit ? (
              <Textarea
                value={axis.notAbout ?? ""}
                onChange={(e) => onChange({ notAbout: e.target.value })}
                placeholder="O que este eixo NÃO mede — para evitar halo bleed com outros eixos."
                disabled={disabled}
                data-testid={`${testidPrefix}-axis-notabout-${axisIdx}`}
                style={structuredTextareaStyle}
              />
            ) : (
              <ReadOnlyText value={axis.notAbout} />
            )}
          </StructuredField>

          {/* baseline */}
          <StructuredField label="Nível-base">
            {isEdit ? (
              <select
                value={axis.baseline != null ? String(axis.baseline) : ""}
                onChange={(e) => {
                  const v = e.target.value;
                  onChange({ baseline: v === "" ? null : Number(v) });
                }}
                disabled={disabled}
                data-testid={`${testidPrefix}-axis-baseline-${axisIdx}`}
                style={{
                  ...UI,
                  fontSize: 13,
                  padding: "7px 10px",
                  borderRadius: 8,
                  border: `1px solid ${axis.baseline != null ? C.ink10 : "rgba(154,123,46,0.5)"}`,
                  background: C.white,
                  color: axis.baseline != null ? C.ink : C.ink50,
                  cursor: disabled ? "not-allowed" : "pointer",
                  width: "100%",
                  maxWidth: 220,
                }}
              >
                <option value="">— (sem nível-base)</option>
                {levels.map((l) => {
                  const name = levelNames?.[String(l)]?.trim();
                  return (
                    <option key={l} value={String(l)}>
                      L{l}
                      {name ? ` · ${name}` : ""}
                    </option>
                  );
                })}
              </select>
            ) : (
              <ReadOnlyText
                value={
                  axis.baseline != null
                    ? `L${axis.baseline}${levelNames?.[String(axis.baseline)]?.trim() ? ` · ${levelNames[String(axis.baseline)]}` : ""}`
                    : undefined
                }
              />
            )}
          </StructuredField>

          {/* falsePositives */}
          <StructuredField label="O que NÃO conta">
            {isEdit ? (
              <Textarea
                value={axis.falsePositives ?? ""}
                onChange={(e) => onChange({ falsePositives: e.target.value })}
                placeholder="Sinais que parecem este eixo mas não devem elevar o nível — prestige traps, volume, título…"
                disabled={disabled}
                data-testid={`${testidPrefix}-axis-falsepos-${axisIdx}`}
                style={structuredTextareaStyle}
              />
            ) : (
              <ReadOnlyText value={axis.falsePositives} />
            )}
          </StructuredField>

          {/* hardCap */}
          <StructuredField label="Teto (hard cap)">
            {isEdit ? (
              <Textarea
                value={axis.hardCap ?? ""}
                onChange={(e) => onChange({ hardCap: e.target.value })}
                placeholder='Regra de teto — ex: "Não passa de L4 sem evidência de ter elevado outros."'
                disabled={disabled}
                data-testid={`${testidPrefix}-axis-hardcap-${axisIdx}`}
                style={structuredTextareaStyle}
              />
            ) : (
              <ReadOnlyText value={axis.hardCap} />
            )}
          </StructuredField>

          {/* cautionaryProfiles */}
          <StructuredField label="Perfis de atenção">
            <CautionaryProfilesList
              profiles={axis.cautionaryProfiles ?? []}
              axisIdx={axisIdx}
              levels={levels}
              levelNames={levelNames}
              disabled={disabled}
              isEdit={isEdit}
              testidPrefix={testidPrefix}
              onChange={(next) => onChange({ cautionaryProfiles: next })}
            />
          </StructuredField>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Cautionary profiles list
// ─────────────────────────────────────────────────────────────────────────────

function CautionaryProfilesList({
  profiles,
  axisIdx,
  levels,
  levelNames,
  disabled,
  isEdit,
  testidPrefix,
  onChange,
}: {
  profiles: CautionaryProfile[];
  axisIdx: number;
  levels: number[];
  levelNames?: Record<string, string>;
  disabled: boolean;
  isEdit: boolean;
  testidPrefix: string;
  onChange: (next: CautionaryProfile[]) => void;
}) {
  function addProfile() {
    const first = levels[0] ?? 0;
    const last = levels[levels.length - 1] ?? 0;
    onChange([...profiles, { profile: "", looksLevel: last, actualLevel: first, why: "" }]);
  }

  function removeProfile(i: number) {
    onChange(profiles.filter((_, j) => j !== i));
  }

  function patchProfile(i: number, patch: Partial<CautionaryProfile>) {
    onChange(profiles.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  }

  if (profiles.length === 0 && !isEdit) {
    return <ReadOnlyText value={undefined} />;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {profiles.map((p, i) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: ordered list; no stable id at authoring time.
          key={i}
          data-testid={`${testidPrefix}-axis-cautionary-${axisIdx}-${i}`}
          style={{
            border: `1px solid ${C.ink10}`,
            borderRadius: 10,
            padding: "12px 14px",
            display: "flex",
            flexDirection: "column",
            gap: 10,
            background: C.paper,
            position: "relative",
          }}
        >
          {isEdit && (
            <button
              type="button"
              onClick={() => removeProfile(i)}
              disabled={disabled}
              aria-label="Remover perfil de atenção"
              style={{
                position: "absolute",
                top: 10,
                right: 10,
                border: `1px solid ${C.ink10}`,
                background: C.white,
                borderRadius: 999,
                width: 26,
                height: 26,
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.5 : 1,
                color: C.violet,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <TrashIcon size={14} />
            </button>
          )}

          {/* profile description */}
          <div>
            <FieldLabel>Perfil</FieldLabel>
            {isEdit ? (
              <Textarea
                value={p.profile}
                onChange={(e) => patchProfile(i, { profile: e.target.value })}
                placeholder='Ex: "VP de Engenharia numa startup de 8 pessoas, stack famosa no CV."'
                disabled={disabled}
                style={{ ...structuredTextareaStyle, minHeight: 64, marginTop: 4 }}
              />
            ) : (
              <ReadOnlyText value={p.profile} />
            )}
          </div>

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {/* looksLevel */}
            <div style={{ flex: 1, minWidth: 120 }}>
              <FieldLabel>Parece</FieldLabel>
              {isEdit ? (
                <select
                  value={String(p.looksLevel)}
                  onChange={(e) => patchProfile(i, { looksLevel: Number(e.target.value) })}
                  disabled={disabled}
                  style={{
                    ...UI,
                    fontSize: 13,
                    padding: "6px 8px",
                    borderRadius: 8,
                    border: `1px solid ${C.ink10}`,
                    background: C.white,
                    color: C.ink,
                    cursor: disabled ? "not-allowed" : "pointer",
                    width: "100%",
                    marginTop: 4,
                  }}
                >
                  {levels.map((l) => {
                    const name = levelNames?.[String(l)]?.trim();
                    return (
                      <option key={l} value={String(l)}>
                        L{l}
                        {name ? ` · ${name}` : ""}
                      </option>
                    );
                  })}
                </select>
              ) : (
                <ReadOnlyText value={`L${p.looksLevel}`} />
              )}
            </div>

            {/* actualLevel */}
            <div style={{ flex: 1, minWidth: 120 }}>
              <FieldLabel>Na verdade</FieldLabel>
              {isEdit ? (
                <select
                  value={String(p.actualLevel)}
                  onChange={(e) => patchProfile(i, { actualLevel: Number(e.target.value) })}
                  disabled={disabled}
                  style={{
                    ...UI,
                    fontSize: 13,
                    padding: "6px 8px",
                    borderRadius: 8,
                    border: `1px solid ${C.ink10}`,
                    background: C.white,
                    color: C.ink,
                    cursor: disabled ? "not-allowed" : "pointer",
                    width: "100%",
                    marginTop: 4,
                  }}
                >
                  {levels.map((l) => {
                    const name = levelNames?.[String(l)]?.trim();
                    return (
                      <option key={l} value={String(l)}>
                        L{l}
                        {name ? ` · ${name}` : ""}
                      </option>
                    );
                  })}
                </select>
              ) : (
                <ReadOnlyText value={`L${p.actualLevel}`} />
              )}
            </div>
          </div>

          {/* why */}
          <div>
            <FieldLabel>Por quê</FieldLabel>
            {isEdit ? (
              <Textarea
                value={p.why}
                onChange={(e) => patchProfile(i, { why: e.target.value })}
                placeholder="Explique a armadilha — o que o CV mostra vs. o que o escopo exige."
                disabled={disabled}
                style={{ ...structuredTextareaStyle, minHeight: 64, marginTop: 4 }}
              />
            ) : (
              <ReadOnlyText value={p.why || undefined} />
            )}
          </div>
        </div>
      ))}

      {isEdit && (
        <button
          type="button"
          onClick={addProfile}
          disabled={disabled}
          style={{
            ...UI,
            border: `1px dashed ${C.ink20}`,
            background: "transparent",
            borderRadius: 8,
            cursor: disabled ? "not-allowed" : "pointer",
            padding: "8px 14px",
            fontSize: 12,
            fontWeight: 600,
            color: C.ink50,
            opacity: disabled ? 0.6 : 1,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <PlusIcon size={13} />
          adicionar perfil de atenção
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-cell structured fields — edit mode
// ─────────────────────────────────────────────────────────────────────────────

function StructuredCellFields({
  axisIdx,
  level,
  cellFields,
  boundaryText,
  isLastLevel,
  disabled,
  testidPrefix,
  onSetFields,
  axisFields,
}: {
  axisIdx: number;
  level: number;
  cellFields: NonNullable<StructuredRubricFields["cells"]>[string];
  boundaryText: string;
  isLastLevel: boolean;
  disabled: boolean;
  testidPrefix: string;
  onSetFields: (next: StructuredRubricFields) => void;
  axisFields: StructuredRubricFields;
}) {
  const referenceProfiles = cellFields.referenceProfiles ?? [];

  return (
    <div
      style={{
        marginTop: 20,
        borderTop: `1px solid ${C.ink5}`,
        paddingTop: 16,
        display: "flex",
        flexDirection: "column",
        gap: 16,
      }}
    >
      {/* whatCounts */}
      <StructuredField label="O que conta">
        <Textarea
          value={cellFields.whatCounts ?? ""}
          onChange={(e) =>
            onSetFields(setStructuredCell(axisFields, level, "whatCounts", e.target.value))
          }
          placeholder="Evidências concretas que colocam alguém neste nível — títulos, escopo, projetos…"
          disabled={disabled}
          data-testid={`${testidPrefix}-cell-whatcounts-${axisIdx}-${level}`}
          style={{ ...structuredTextareaStyle, minHeight: 80 }}
        />
      </StructuredField>

      {/* easyToMiss */}
      <StructuredField label="Fácil de não ver">
        <Textarea
          value={cellFields.easyToMiss ?? ""}
          onChange={(e) =>
            onSetFields(setStructuredCell(axisFields, level, "easyToMiss", e.target.value))
          }
          placeholder="Evidências válidas mas em formato incomum — pode ser subestimado."
          disabled={disabled}
          data-testid={`${testidPrefix}-cell-easytomiss-${axisIdx}-${level}`}
          style={{ ...structuredTextareaStyle, minHeight: 64 }}
        />
      </StructuredField>

      {/* Reference profiles */}
      <StructuredField label="Perfis de referência">
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {referenceProfiles.map((rp, i) => (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: ordered list; no stable id at authoring time.
              key={i}
              data-testid={`${testidPrefix}-cell-refprofile-${axisIdx}-${level}-${i}`}
              style={{
                border: `1px solid ${C.ink10}`,
                borderRadius: 10,
                padding: "10px 12px",
                display: "flex",
                flexDirection: "column",
                gap: 8,
                background: C.paper,
                position: "relative",
              }}
            >
              <button
                type="button"
                onClick={() => onSetFields(removeReferenceProfileIn(axisFields, level, i))}
                disabled={disabled}
                aria-label="Remover perfil de referência"
                style={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  border: `1px solid ${C.ink10}`,
                  background: C.white,
                  borderRadius: 999,
                  width: 24,
                  height: 24,
                  cursor: disabled ? "not-allowed" : "pointer",
                  opacity: disabled ? 0.5 : 1,
                  color: C.violet,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <TrashIcon size={12} />
              </button>

              <div style={{ paddingRight: 28 }}>
                <FieldLabel>Nome</FieldLabel>
                <Input
                  value={rp.name}
                  onChange={(e) =>
                    onSetFields(
                      patchReferenceProfileIn(axisFields, level, i, { name: e.target.value }),
                    )
                  }
                  placeholder="Nome ou descrição do perfil"
                  disabled={disabled}
                  style={{ ...structuredInputStyle, marginTop: 4 }}
                />
              </div>
              <div>
                <FieldLabel>URL (LinkedIn, etc.)</FieldLabel>
                <Input
                  value={rp.url ?? ""}
                  onChange={(e) =>
                    onSetFields(
                      patchReferenceProfileIn(axisFields, level, i, { url: e.target.value }),
                    )
                  }
                  placeholder="https://linkedin.com/in/…"
                  disabled={disabled}
                  style={{ ...structuredInputStyle, marginTop: 4 }}
                />
              </div>
              <div>
                <FieldLabel>Por quê é referência aqui</FieldLabel>
                <Textarea
                  value={rp.reason}
                  onChange={(e) =>
                    onSetFields(
                      patchReferenceProfileIn(axisFields, level, i, { reason: e.target.value }),
                    )
                  }
                  placeholder="O que este perfil exemplifica neste nível."
                  disabled={disabled}
                  style={{ ...structuredTextareaStyle, minHeight: 56, marginTop: 4 }}
                />
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={() => onSetFields(addReferenceProfileIn(axisFields, level))}
            disabled={disabled}
            style={{
              ...UI,
              border: `1px dashed ${C.ink20}`,
              background: "transparent",
              borderRadius: 8,
              cursor: disabled ? "not-allowed" : "pointer",
              padding: "7px 12px",
              fontSize: 12,
              fontWeight: 600,
              color: C.ink50,
              opacity: disabled ? 0.6 : 1,
              display: "flex",
              alignItems: "center",
              gap: 5,
            }}
          >
            <PlusIcon size={13} />
            adicionar perfil de referência
          </button>
        </div>
      </StructuredField>

      {/* "Cruza quando" boundary — only shown for non-last levels */}
      {!isLastLevel && (
        <StructuredField label={`Cruza quando (L${level} → L${level + 1})`}>
          <Textarea
            value={boundaryText}
            onChange={(e) => onSetFields(setBoundaryIn(axisFields, level, e.target.value))}
            placeholder={`O que muda na prática quando alguém sobe de L${level} para L${level + 1} neste eixo.`}
            disabled={disabled}
            data-testid={`${testidPrefix}-boundary-${axisIdx}-${level}`}
            style={{ ...structuredTextareaStyle, minHeight: 80 }}
          />
        </StructuredField>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-cell structured fields — review (read-only) mode
// ─────────────────────────────────────────────────────────────────────────────

function StructuredCellFieldsReadOnly({
  axisIdx,
  level,
  cellFields,
  boundaryText,
  isLastLevel,
  testidPrefix,
}: {
  axisIdx: number;
  level: number;
  cellFields: NonNullable<StructuredRubricFields["cells"]>[string];
  boundaryText: string;
  isLastLevel: boolean;
  testidPrefix: string;
}) {
  const referenceProfiles = cellFields.referenceProfiles ?? [];
  const hasAny =
    cellFields.whatCounts?.trim() ||
    cellFields.easyToMiss?.trim() ||
    referenceProfiles.length > 0 ||
    (!isLastLevel && boundaryText.trim());

  if (!hasAny) return null;

  return (
    <div
      style={{
        marginTop: 20,
        borderTop: `1px solid ${C.ink5}`,
        paddingTop: 16,
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      {cellFields.whatCounts?.trim() && (
        <StructuredField label="O que conta">
          <div
            data-testid={`${testidPrefix}-cell-whatcounts-${axisIdx}-${level}`}
            style={{
              ...DM,
              fontSize: 14,
              lineHeight: "22px",
              color: C.ink,
              whiteSpace: "pre-wrap",
            }}
          >
            {cellFields.whatCounts}
          </div>
        </StructuredField>
      )}

      {cellFields.easyToMiss?.trim() && (
        <StructuredField label="Fácil de não ver">
          <div
            data-testid={`${testidPrefix}-cell-easytomiss-${axisIdx}-${level}`}
            style={{
              ...DM,
              fontSize: 14,
              lineHeight: "22px",
              color: C.ink,
              whiteSpace: "pre-wrap",
            }}
          >
            {cellFields.easyToMiss}
          </div>
        </StructuredField>
      )}

      {referenceProfiles.length > 0 && (
        <StructuredField label="Perfis de referência">
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {referenceProfiles.map((rp, i) => (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: ordered list; no stable id.
                key={i}
                data-testid={`${testidPrefix}-cell-refprofile-${axisIdx}-${level}-${i}`}
                style={{
                  border: `1px solid ${C.ink10}`,
                  borderRadius: 8,
                  padding: "8px 12px",
                  background: C.paper,
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                <span style={{ ...DM, fontSize: 13, fontWeight: 600, color: C.ink }}>
                  {rp.name || "—"}
                  {rp.url && (
                    <a
                      href={rp.url.startsWith("http") ? rp.url : `https://${rp.url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ ...UI, fontSize: 11, color: C.red, marginLeft: 8 }}
                    >
                      ↗
                    </a>
                  )}
                </span>
                {rp.reason && (
                  <span style={{ ...UI, fontSize: 12, color: C.ink70, lineHeight: "18px" }}>
                    {rp.reason}
                  </span>
                )}
              </div>
            ))}
          </div>
        </StructuredField>
      )}

      {!isLastLevel && boundaryText.trim() && (
        <StructuredField label={`Cruza quando (L${level} → L${level + 1})`}>
          <div
            data-testid={`${testidPrefix}-boundary-${axisIdx}-${level}`}
            style={{
              ...DM,
              fontSize: 14,
              lineHeight: "22px",
              color: C.ink,
              whiteSpace: "pre-wrap",
            }}
          >
            {boundaryText}
          </div>
        </StructuredField>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Small shared primitives
// ─────────────────────────────────────────────────────────────────────────────

/** Labelled wrapper for a structured pillar field. */
function StructuredField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <FieldLabel>{label}</FieldLabel>
      {children}
    </div>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        ...UI,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        color: C.ink50,
      }}
    >
      {children}
    </span>
  );
}

function ReadOnlyText({ value }: { value: string | undefined | null }) {
  if (!value?.trim()) {
    return <span style={{ ...UI, fontSize: 13, color: C.ink50, fontStyle: "italic" }}>—</span>;
  }
  return (
    <div
      style={{
        ...DM,
        fontSize: 14,
        lineHeight: "22px",
        color: C.ink,
        whiteSpace: "pre-wrap",
      }}
    >
      {value}
    </div>
  );
}

/** Shared textarea style for all structured fields — matches existing visual language. */
const structuredTextareaStyle: React.CSSProperties = {
  ...DM,
  width: "100%",
  minHeight: 72,
  resize: "vertical",
  padding: "10px 12px",
  borderRadius: 8,
  border: `1px solid ${C.ink10}`,
  fontSize: 13,
  lineHeight: "20px",
  color: C.ink,
};

/** Shared input style for reference profile text inputs. */
const structuredInputStyle: React.CSSProperties = {
  ...UI,
  width: "100%",
  fontSize: 13,
  borderRadius: 8,
  border: `1px solid ${C.ink10}`,
  padding: "6px 10px",
  height: "auto",
  color: C.ink,
};

// ─────────────────────────────────────────────────────────────────────────────
// Existing sub-components (unchanged)
// ─────────────────────────────────────────────────────────────────────────────

function AxisWorkspaceHeader({
  axis,
  axisIdx,
  filled,
  levelCount,
  canRemove,
  disabled,
  testidPrefix,
  onChange,
  onRemove,
}: {
  axis: AxisDraft;
  axisIdx: number;
  filled: number;
  levelCount: number;
  canRemove: boolean;
  disabled: boolean;
  testidPrefix: string;
  onChange: (patch: Partial<AxisDraft>) => void;
  onRemove: () => void;
}) {
  const weightInvalid = !(axis.weight > 0) || !Number.isFinite(axis.weight);
  return (
    <div style={{ padding: "18px 24px", borderBottom: `1px solid ${C.borderSoft}` }}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 16,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <Input
            value={axis.label}
            onChange={(e) => onChange({ label: e.target.value })}
            placeholder="Label do eixo"
            disabled={disabled}
            data-testid={`${testidPrefix}-axis-label-${axisIdx}`}
            style={{
              ...DM,
              width: "100%",
              border: 0,
              borderBottom: "1px solid transparent",
              borderRadius: 0,
              height: "auto",
              padding: "0 0 3px 0",
              fontWeight: 700,
              fontSize: 22,
              letterSpacing: "-0.01em",
              color: C.ink,
              background: "transparent",
            }}
          />
          <Input
            value={axis.description}
            onChange={(e) => onChange({ description: e.target.value })}
            placeholder="O que esse eixo mede (uma linha)"
            disabled={disabled}
            data-testid={`${testidPrefix}-axis-description-${axisIdx}`}
            style={{
              ...UI,
              width: "100%",
              border: 0,
              borderRadius: 0,
              height: "auto",
              padding: 0,
              marginTop: 4,
              fontSize: 13.5,
              color: C.ink70,
              background: "transparent",
            }}
          />
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flex: "none",
            paddingTop: 2,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              background: C.paper,
              borderRadius: 999,
              padding: "5px 7px 5px 12px",
            }}
          >
            <span style={{ ...UI, fontSize: 12, fontWeight: 600, color: C.ink70 }}>Peso</span>
            <Input
              type="number"
              value={axis.weight}
              min={0.5}
              step={0.5}
              onChange={(e) => {
                const v = e.target.valueAsNumber;
                onChange({ weight: Number.isNaN(v) ? 0 : v });
              }}
              disabled={disabled}
              data-testid={`${testidPrefix}-axis-weight-${axisIdx}`}
              style={{
                ...MONO,
                width: 52,
                height: "auto",
                borderRadius: 7,
                border: `1px solid ${weightInvalid ? C.red : C.ink10}`,
                padding: "4px",
                fontSize: 13,
                color: C.ink,
                textAlign: "center",
                background: C.white,
              }}
            />
          </div>
          <button
            type="button"
            onClick={onRemove}
            disabled={!canRemove || disabled}
            aria-label="Remover eixo"
            data-testid={`${testidPrefix}-axis-remove-${axisIdx}`}
            title={canRemove ? "Remover este eixo" : "Pelo menos um eixo é obrigatório"}
            style={{
              border: `1px solid ${C.ink10}`,
              background: C.white,
              borderRadius: 999,
              width: 34,
              height: 34,
              cursor: canRemove && !disabled ? "pointer" : "default",
              opacity: canRemove && !disabled ? 1 : 0.3,
              color: C.violet,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: "none",
            }}
          >
            <TrashIcon />
          </button>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12 }}>
        <span style={{ ...UI, fontSize: 11, color: C.ink50 }}>
          Peso relativo — normalizado em runtime.
        </span>
        <span style={{ flex: 1 }} />
        <span style={{ ...MONO, fontSize: 11, color: C.ink50 }}>
          {filled} de {levelCount} níveis
        </span>
      </div>
    </div>
  );
}

/** Per-cell soft-target progress bar + counter shown below the edit textarea. */
function CellProgress({ len }: { len: number }) {
  const pct = Math.min(1, len / CELL_TARGET);
  const reached = len >= CELL_TARGET * 0.85;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12 }}>
      <div
        style={{
          flex: 1,
          maxWidth: 340,
          height: 5,
          borderRadius: 999,
          background: C.ink5,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${Math.round(pct * 100)}%`,
            background: reached ? C.green : C.red,
            transition: "width 200ms",
          }}
        />
      </div>
      <span style={{ ...MONO, fontSize: 11, color: C.ink50 }}>
        {len} / {CELL_TARGET}
      </span>
    </div>
  );
}

function SegmentButton({
  children,
  active,
  activeBg,
  onClick,
  disabled = false,
  testId,
}: {
  children: React.ReactNode;
  active: boolean;
  activeBg: string;
  onClick: () => void;
  disabled?: boolean;
  testId: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      style={{
        ...UI,
        display: "flex",
        alignItems: "center",
        gap: 6,
        border: 0,
        cursor: disabled ? "not-allowed" : "pointer",
        borderRadius: 999,
        padding: "6px 14px",
        fontSize: 13,
        fontWeight: 600,
        background: active ? activeBg : "transparent",
        color: active ? C.white : C.ink70,
        boxShadow: active ? "0 1px 2px rgba(0,0,0,0.1)" : "none",
        transition: "all 160ms",
      }}
    >
      {children}
    </button>
  );
}

function PlusIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function ChevronIcon({ rotated, color }: { rotated: boolean; color: string }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{
        flex: "none",
        transform: `rotate(${rotated ? 180 : 0}deg)`,
        transition: "transform 200ms",
      }}
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

function TrashIcon({ size = 17 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
    </svg>
  );
}
